"use client";

import { usePathname } from "next/navigation";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import Markdown from "react-markdown";
import { toast } from "sonner";
import useSWR from "swr";
import { ContentLoader } from "@/components/content-loader";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSafeTranslations } from "@/hooks/use-safe-translation";
import { useMutation, useUser } from "@/lib";

type TosPrivacyKey = "tos" | "pap";

export type TosPrivacyState = Record<TosPrivacyKey, boolean>;

interface Props {
	children: ReactNode;
}

const TermsAndPolicyProvider = ({ children }: Props) => {
	const t = useSafeTranslations();
	const pathname = usePathname();
	const isAuthPage = pathname.startsWith("/auth");
	const { user, logIn } = useUser();
	const [isTosAndPap, setIsTosAndPap] = useState<TosPrivacyState>({
		tos: false,
		pap: false,
	});
	const [version, setVersion] = useState({
		tos: "",
		pap: "",
	});

	const handleCheckBox = useCallback((key: TosPrivacyKey, val: boolean) => {
		setIsTosAndPap((prev) => ({
			...prev,
			[key]: val,
		}));
	}, []);

	const handleSetVersion = useCallback((key: TosPrivacyKey, val: string) => {
		setVersion((prev) => ({
			...prev,
			[key]: val,
		}));
	}, []);

	const { trigger, isMutating } = useMutation("user/docs/accept");

	const handleMutateTosAndPap = useCallback(async () => {
		try {
			const res = (await trigger({
				method: "POST",
				json: { tos_version: version.tos, privacy_policy_version: version.pap },
			})) as {
				data: { privacy_policy_accepted: boolean; tos_accepted: boolean };
			};
			if (!res.data.privacy_policy_accepted || !res.data.tos_accepted) {
				toast.error(t("components.tos_and_pap.messages.error"));
				return;
			}
			if (user) {
				logIn({
					...user,
					tos_accepted: res.data.tos_accepted,
					privacy_policy_accepted: res.data.privacy_policy_accepted,
				});
			}
			toast.success(t("components.tos_and_pap.messages.success"));
		} catch {
			toast.error(t("components.tos_and_pap.messages.error"));
		}
	}, [trigger, version.tos, version.pap, t, user, logIn]);

	if (isAuthPage) {
		return children;
	}

	if (user && (!user.tos_accepted || !user.privacy_policy_accepted)) {
		return (
			<TosAndPrivacy
				state={isTosAndPap}
				isMutating={isMutating}
				handleCheckBox={handleCheckBox}
				handleMutateTosAndPap={handleMutateTosAndPap}
				handleSetVersion={handleSetVersion}
			/>
		);
	}

	return children;
};

export default TermsAndPolicyProvider;

function TosAndPrivacy({
	state,
	isMutating,
	handleCheckBox,
	handleMutateTosAndPap,
	handleSetVersion,
}: {
	state: TosPrivacyState;
	isMutating: boolean;
	handleCheckBox: (key: TosPrivacyKey, val: boolean) => void;
	handleMutateTosAndPap: () => Promise<void> | void;
	handleSetVersion: (key: TosPrivacyKey, val: string) => void;
}) {
	const t = useSafeTranslations();
	const [tab, setTab] = useState("tos");

	const handleTosCheck = useCallback(
		(val: boolean) => {
			handleCheckBox("tos", val);
			if (val) setTab("pap");
		},
		[handleCheckBox],
	);

	const handlePapCheck = useCallback(
		(val: boolean) => {
			handleCheckBox("pap", val);
		},
		[handleCheckBox],
	);

	return (
		<main className="p-12 flex flex-col gap-6">
			<Tabs value={tab} onValueChange={setTab}>
				<TabsList variant={"line"} className="mb-3.5 w-full">
					<TabsTrigger value="tos">
						{t("components.tos_and_pap.tos.title")}
					</TabsTrigger>
					<TabsTrigger value="pap">
						{t("components.tos_and_pap.pap.title")}
					</TabsTrigger>
				</TabsList>
				<TabsContent value="tos">
					<DocTab
						docKey="tos"
						path="tnc"
						checked={state.tos}
						onCheckedChange={handleTosCheck}
						handleSetVersion={handleSetVersion}
					/>
				</TabsContent>
				<TabsContent value="pap">
					<DocTab
						docKey="pap"
						path="privacy-policy"
						checked={state.pap}
						onCheckedChange={handlePapCheck}
						handleSetVersion={handleSetVersion}
					/>
				</TabsContent>
			</Tabs>
			<section className="flex self-end">
				<Button
					disabled={!state.tos || !state.pap || isMutating}
					onClick={handleMutateTosAndPap}
				>
					{t("components.tos_and_pap.button")}
				</Button>
			</section>
		</main>
	);
}

function DocTab({
	docKey,
	path,
	checked,
	onCheckedChange,
	handleSetVersion,
}: {
	docKey: TosPrivacyKey;
	path: "tnc" | "privacy-policy";
	checked: boolean;
	onCheckedChange: (val: boolean) => void;
	handleSetVersion: (key: TosPrivacyKey, val: string) => void;
}) {
	const t = useSafeTranslations();
	const [scrolled, setScrolled] = useState(false);
	const viewportRef = useRef<HTMLDivElement>(null);

	const checkScroll = useCallback(() => {
		const el = viewportRef.current;
		if (!el) return;
		if (el.scrollHeight - el.scrollTop - el.clientHeight < 10) {
			setScrolled(true);
		}
	}, []);

	// Ref callback instead of an effect: fires whenever the content node is
	// (re)mounted, so there is no ordering race with the tab switch. The
	// observer re-checks once the markdown replaces the loader.
	const contentRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (!node) return;
			const observer = new ResizeObserver(checkScroll);
			observer.observe(node);
			checkScroll();
			return () => observer.disconnect();
		},
		[checkScroll],
	);

	return (
		<Card>
			<CardHeader className="flex flex-col justify-start items-start">
				<CardTitle className="text-base">
					{t(`components.tos_and_pap.${docKey}.title`)}
				</CardTitle>
				<CardDescription className="text-sm">
					{t(`components.tos_and_pap.${docKey}.description`)}
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<ScrollArea
					className="h-[50vh]"
					viewportRef={viewportRef}
					onViewportScroll={checkScroll}
				>
					<div ref={contentRef}>
						<Content path={path} handleSetVersion={handleSetVersion} />
					</div>
				</ScrollArea>
			</CardContent>
			<CardFooter className="pt-3 flex flex-row gap-3 items-center">
				<Checkbox
					id={`${docKey}-acc`}
					checked={checked}
					onCheckedChange={(val) => onCheckedChange(!!val)}
					disabled={!scrolled}
				/>
				<Label htmlFor={`${docKey}-acc`}>
					{t(`components.tos_and_pap.${docKey}.checkbox`)}
				</Label>
			</CardFooter>
		</Card>
	);
}

function Content({
	path,
	handleSetVersion,
}: {
	path: "tnc" | "privacy-policy";
	handleSetVersion: (key: TosPrivacyKey, val: string) => void;
}) {
	const { data, isLoading } = useSWR<{
		data: { content: string; version: string };
	}>(path);

	useEffect(() => {
		if (!isLoading && data?.data.version) {
			handleSetVersion(path === "tnc" ? "tos" : "pap", data.data.version);
		}
	}, [isLoading, data?.data.version, path, handleSetVersion]);

	if (isLoading) {
		return <ContentLoader />;
	}

	return <Markdown>{data?.data.content || ""}</Markdown>;
}
